import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import i18n from "../../i18n/i18n";
import { AUTHORITY_PAGES, CASE_STUDIES } from "../../data/authorityPages";

const SITE_URL = "https://emmpieza.com";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

function getOrganization() {
    const t = i18n.t.bind(i18n);
    return {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "EMMPIEZA",
        legalName: "EMMPIEZA COMUNICACIONES S.A.S",
        url: SITE_URL,
        logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
        description: t("seo.home.description"),
        foundingDate: "2019",
        email: "emmpieza@gmail.com",
        areaServed: { "@type": "Country", name: "Colombia" },
        subjectOf: {
            "@type": "DigitalDocument",
            name: "Boletín de registros de la Cámara de Comercio de Bogotá — agosto de 2019",
            url: "https://bibliotecadigital.ccb.org.co/server/api/core/bitstreams/88634c24-59aa-45fe-84c6-8414610c21d2/content",
        },
        knowsAbout: t("seo.knowsAbout", { returnObjects: true }),
        founder: {
            "@type": "Person",
            name: "Raúl Andrés Fajardo Murillo",
            jobTitle: t("seo.founderJobTitle"),
            sameAs: "https://www.linkedin.com/in/raulandresfajardo/",
        },
    };
}

const ROUTE_SEO_KEYS = {
    "/": "seo.home",
    "/nosotros": "seo.nosotros",
    "/servicios/inteligencia-artificial": "seo.ia",
    "/servicios/software-y-aplicaciones": "seo.software",
    "/servicios/ciberseguridad": "seo.ciber",
    "/ia-aviacion-seguridad-operacional": "seo.aviacion",
    "/proyectos": "seo.proyectos",
    "/blog": "seo.blog",
    "/analiza-tu-cv": "seo.cv",
    "/contacto": "seo.contacto",
    "/politica-privacidad": "seo.privacidad",
    "/politica-datos": "seo.datos",
};

const ROUTE_META = {
    "/": {},
    "/nosotros": { type: "AboutPage" },
    "/servicios/inteligencia-artificial": { type: "Service" },
    "/servicios/software-y-aplicaciones": { type: "Service" },
    "/servicios/ciberseguridad": { type: "Service" },
    "/ia-aviacion-seguridad-operacional": { type: "Service" },
    "/proyectos": { type: "CollectionPage" },
    "/blog": { type: "Blog" },
    "/analiza-tu-cv": { type: "WebPage" },
    "/contacto": { type: "ContactPage" },
    "/politica-privacidad": { robots: "noindex,follow" },
    "/politica-datos": { robots: "noindex,follow" },
};

export function getSeoPages() {
    const t = i18n.t.bind(i18n);
    const pages = {};
    Object.entries(ROUTE_SEO_KEYS).forEach(([path, key]) => {
        const meta = ROUTE_META[path] || {};
        pages[path] = {
            title: t(`${key}.title`),
            description: t(`${key}.description`),
            ...meta,
            ...(meta.type === "Service" ? { serviceType: t(`${key}.serviceType`) } : {}),
        };
    });

    Object.entries(AUTHORITY_PAGES).forEach(([slug, page]) => {
        pages[page.path] = {
            title: `${t(`authority.pages.${slug}.title`)} | EMMPIEZA`,
            description: t(`authority.pages.${slug}.description`),
            type: "TechArticle",
            citation: page.sources.map(([, url]) => url),
        };
    });

    Object.entries(CASE_STUDIES).forEach(([slug, page]) => {
        pages[page.path] = {
            title: `${t(`authority.cases.${slug}.title`)} | EMMPIEZA`,
            description: t(`authority.cases.${slug}.description`),
            type: "Article",
            citation: (page.sources || []).map(([, url]) => url),
        };
    });

    return pages;
}

// Kept for backward compatibility with any direct imports; always reflects
// the currently active language at import/evaluation time.
export const SEO_PAGES = new Proxy(
    {},
    {
        get(_target, prop) {
            return getSeoPages()[prop];
        },
        ownKeys() {
            return Reflect.ownKeys(getSeoPages());
        },
        getOwnPropertyDescriptor(_target, prop) {
            const pages = getSeoPages();
            if (prop in pages) {
                return { enumerable: true, configurable: true, value: pages[prop] };
            }
            return undefined;
        },
    },
);

function setMeta(selector, attribute, value) {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement("meta");
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match) element.setAttribute(match[1], match[2]);
        document.head.appendChild(element);
    }
    element.setAttribute(attribute, value);
}

function setLink(rel, href) {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
    }
    element.setAttribute("href", href);
}

export function applySeo({
    title,
    description,
    path,
    type = "WebPage",
    serviceType,
    image = DEFAULT_IMAGE,
    robots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    article,
    citation = [],
}) {
    const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="robots"]', "content", robots);
    setMeta('meta[name="author"]', "content", "EMMPIEZA");
    setLink("canonical", canonical);

    const isEnglish = i18n.language?.startsWith("en");
    const ogLocale = isEnglish ? "en_US" : "es_CO";
    const inLanguage = isEnglish ? "en-US" : "es-CO";
    const organization = getOrganization();

    const social = {
        'meta[property="og:locale"]': ogLocale,
        'meta[property="og:type"]': article || ["Article", "TechArticle"].includes(type) ? "article" : "website",
        'meta[property="og:site_name"]': "EMMPIEZA",
        'meta[property="og:title"]': title,
        'meta[property="og:description"]': description,
        'meta[property="og:url"]': canonical,
        'meta[property="og:image"]': image,
        'meta[name="twitter:card"]': "summary_large_image",
        'meta[name="twitter:title"]': title,
        'meta[name="twitter:description"]': description,
        'meta[name="twitter:image"]': image,
    };
    Object.entries(social).forEach(([selector, value]) =>
        setMeta(selector, "content", value),
    );

    let entity;
    if (article) {
        const articleAuthor = {
            "@type": "Person",
            name: article.author || "EMMPIEZA",
            ...(article.authorProfile ? { sameAs: article.authorProfile } : {}),
            ...(article.authorRole ? { jobTitle: article.authorRole } : {}),
        };
        entity = {
            "@type": "BlogPosting",
            headline: article.headline,
            description,
            url: canonical,
            mainEntityOfPage: canonical,
            image,
            datePublished: article.datePublished,
            dateModified: article.dateModified,
            inLanguage,
            author: articleAuthor,
            publisher: { "@id": organization["@id"] },
            ...(article.citation?.length ? { citation: article.citation } : {}),
        };
    } else if (type === "Service") {
        entity = {
            "@type": "Service",
            name: title.split("|")[0].trim(),
            serviceType,
            description,
            url: canonical,
            provider: { "@id": organization["@id"] },
            areaServed: { "@type": "Country", name: "Colombia" },
        };
    } else {
        entity = {
            "@type": type,
            name: title,
            description,
            url: canonical,
            inLanguage,
            about: { "@id": organization["@id"] },
            ...(citation.length ? { citation } : {}),
            ...(["Article", "TechArticle"].includes(type) ? {
                headline: title.split("|")[0].trim(),
                author: { "@id": organization["@id"] },
                publisher: { "@id": organization["@id"] },
            } : {}),
        };
    }

    let script = document.getElementById("emmpieza-schema");
    if (!script) {
        script = document.createElement("script");
        script.id = "emmpieza-schema";
        script.type = "application/ld+json";
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [organization, entity],
    });
}

export function Seo(props) {
    useEffect(() => {
        applySeo(props);
        const onLangChange = () => applySeo(props);
        i18n.on("languageChanged", onLangChange);
        return () => i18n.off("languageChanged", onLangChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);
    return null;
}

export default function RouteSeo() {
    const { pathname } = useLocation();
    useEffect(() => {
        const applyForPath = () => {
            const normalized = pathname.replace(/\/$/, "") || "/";
            const page = getSeoPages()[normalized];
            if (page) applySeo({ ...page, path: normalized });
        };
        applyForPath();
        i18n.on("languageChanged", applyForPath);
        return () => i18n.off("languageChanged", applyForPath);
    }, [pathname]);
    return null;
}
